import React from 'react';
import styled from 'styled-components';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import { TranslateFunc } from '../common/types';

// inUse + available should be <= total
// Bad data will result in bad UI - we still attempt to process it but the paths of the slices might not be arcs
// NOTE: inUse and available could be % of total instead - TBD
export interface KitClusterCircleProps extends React.ComponentPropsWithoutRef<'div'> {
  available?: number;
  inUse?: number;
  total: number;
  bgColor?: string; // defaults to white
  focusable?: boolean;
  id: string;
  onClick?: () => void;
  t: TranslateFunc;
}

interface CSSProps {
  size: number;
}

const Container = styled.div<CSSProps>`
  height: ${({ size }) => size}px;
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const PieChart = styled.svg`
  height: 100%;
  outline: none;
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-weight: ${constants.fontWeight.normal};
  color: ${({ theme }) => theme.page.body.text};

  & text {
    font-size: 24px !important;
  }
`;

const KitClusterCircle: React.FC<KitClusterCircleProps> = ({
  available: availablePorts = 0,
  inUse: usedPorts = 0,
  total: totalPorts = 0,
  bgColor = colors.white,
  focusable,
  id,
  onClick,
  t,
  ...rest
}: KitClusterCircleProps) => {
  const label = totalPorts.toString();
  const labelLen = label.length;
  const radius = 30 + (labelLen - 1) * 10;
  const outerRadius = radius + 3;

  const slices = [
    { percent: availablePorts / totalPorts, color: colors.green_30 },
    { percent: usedPorts / totalPorts, color: colors.blue_60 },
    {
      percent: (totalPorts - availablePorts - usedPorts) / totalPorts,
      color: colors.gray_30,
    },
  ];
  const pieSlices = [];

  let cumulativePercent = 0;

  // Add 75% to all percentages so that it starts at 12:00 instead of 3:00
  function getCoordinatesForPercent(percent) {
    const x = Math.cos(2 * Math.PI * (percent + 0.75)) * radius;
    const y = Math.sin(2 * Math.PI * (percent + 0.75)) * radius;
    return [x, y];
  }

  const onClickHandler = () => {
    if (onClick) {
      onClick();
    }
  };

  slices.forEach((slice, index) => {
    const sliceId = `${id}_inner_${index}`;
    if (slice.percent) {
      if (slice.percent === 1) {
        pieSlices.push(
          <circle id={sliceId} key={sliceId} r={radius} cx={0} cy={0} fill={slice.color} />
        );
      } else {
        const gap = 0.02;
        const percentWithGap = slice.percent - gap;
        // If the slice is more than 50%, take the large arc (the long way around)
        const largeArcFlag = percentWithGap > 0.5 ? 1 : 0;

        const [startX, startY] = getCoordinatesForPercent(cumulativePercent + gap / 2);

        // Each slice starts where the last slice ended, so keep a cumulative percent
        cumulativePercent += percentWithGap;
        const [endX, endY] = getCoordinatesForPercent(cumulativePercent + gap / 2);

        const pathData = [
          `M ${startX} ${startY}`, // Move
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
          'L 0 0', // Line back to center
        ].join(' ');

        pieSlices.push(
          <path
            id={`${id}_pie_${index}`}
            key={`${id}_pie_${slice.color}`}
            d={pathData}
            fill={slice.color}
            stroke={bgColor}
            strokeWidth={0}
          />
        );

        if (gap) {
          cumulativePercent += gap;
          const [endGapX, endGapY] = getCoordinatesForPercent(cumulativePercent);

          const gapPathData = [
            `M ${endX} ${endY}`, // Move
            `A ${radius} ${radius} 0 0 1 ${endGapX} ${endGapY}`, // Arc
            'L 0 0', // Line back to center
          ].join(' ');

          pieSlices.push(
            <path
              id={`${id}_gap_${index}`}
              key={`${id}_gap_${slice.color}`}
              d={gapPathData}
              fill={bgColor}
              stroke={bgColor}
              strokeWidth={0}
            />
          );
        }
      }
    }
  });

  return (
    <Container size={outerRadius} onClick={onClickHandler} {...rest}>
      <PieChart
        role='img'
        focusable={focusable}
        aria-labelledby={id}
        viewBox={`-${outerRadius + 1} -${outerRadius + 1} ${outerRadius * 2 + 2} ${
          outerRadius * 2 + 2
        }`}
      >
        <title id={id}>
          {t('kit:cluster_circle.title', {
            total: totalPorts,
            available: availablePorts,
            in_use: usedPorts,
          })}
        </title>
        <circle
          id={`${id}_outer`}
          r={outerRadius}
          cx={0}
          cy={0}
          stroke={colors.gray_30}
          strokeWidth='1'
          fill={bgColor}
        />
        {pieSlices}
        <circle
          id={`${id}_innerCircle`}
          r={radius - 10 - (labelLen - 1) * 3}
          cx={0}
          cy={0}
          fill={bgColor}
        />
        <text
          textAnchor='middle'
          alignmentBaseline='middle'
          stroke={colors.gray_70}
          strokeWidth='1'
          dy='.5'
        >
          {totalPorts}
        </text>
      </PieChart>
    </Container>
  );
};

export default KitClusterCircle;
